import type { StageKind, Treatment, Patient } from '../../types/admin';

export type ClientStatusItemState = 'complete' | 'attention';

export type ClientStatusAction = {
  readonly section: 'treatments' | 'treatment-data' | 'tooth-spacing' | 'payments';
  readonly targetId?: string;
  readonly treatmentId?: string;
  readonly stageId?: string;
  readonly stageKind?: StageKind;
  readonly openCreateTreatment?: boolean;
};

export type ClientStatusItemKey =
  | 'spacingMapReady'
  | 'spacingMapMissing'
  | 'activeTreatmentReady'
  | 'activeTreatmentMissing'
  | 'diagnosisReady'
  | 'diagnosisMissing'
  | 'activeCycleReady'
  | 'activeCycleMissing'
  | 'completedStage'
  | 'blockedStage'
  | 'currentStage'
  | 'requiredProcedureReady'
  | 'requiredProcedureNeedsVerification'
  | 'requiredProcedurePending'
  | 'requiredProceduresMissing'
  | 'modelingPlanReady'
  | 'modelingPlanMissing'
  | 'publishedPlanReady'
  | 'publishedPlanMissing'
  | 'planApprovedByPatient'
  | 'planPendingPatientApproval'
  | 'paymentStageCurrent'
  | 'deliveryReady'
  | 'deliveryMissing'
  | 'trackingReady'
  | 'trackingMissing'
  | 'deliveryShipped'
  | 'deliveryDelivered'
  | 'deliveryCancelled'
  | 'alignerSetsReady'
  | 'alignerSetsMissing'
  | 'wearingStarted'
  | 'wearingNotStarted';

export type ClientStatusItem = {
  readonly id: string;
  readonly state: ClientStatusItemState;
  readonly key: ClientStatusItemKey;
  readonly values?: Readonly<Record<string, string | number>>;
  readonly action?: ClientStatusAction;
};

const STAGE_LABEL_KEYS: Record<StageKind, string> = {
  preparing: 'stageEditor.preparing',
  modeling: 'stageEditor.modeling',
  payment: 'stageEditor.payment',
  manufacturing_delivery: 'stageEditor.delivery',
  wearing_aligners: 'stageEditor.wearing',
};

type ClientStatusPatient = Pick<Patient, 'tooth_spacing_map_url'>;

function item(
  id: string,
  state: ClientStatusItemState,
  key: ClientStatusItemKey,
  options: Pick<ClientStatusItem, 'values' | 'action'> = {},
): ClientStatusItem {
  return { id, state, key, ...options };
}

function stageAction(
  treatmentId: string,
  stageId: string,
  stageKind: StageKind,
): ClientStatusAction {
  return {
    section: stageKind === 'payment' ? 'payments' : 'treatments',
    targetId: stageKind === 'payment' ? 'payments-tab' : `${stageId}-${stageKind}-tab`,
    treatmentId,
    stageId,
    stageKind,
  };
}

function hasDiagnosis(treatment: Treatment): boolean {
  if (treatment.diagnosis_titles?.some((title) => title.trim().length > 0)) return true;
  return Boolean(treatment.diagnosis_title?.trim());
}

function hasUsablePublishedPlan(stage: Treatment['stages'][number]): boolean {
  return (stage.plan_options ?? []).some(
    (plan) => plan.is_published && (plan.upper_aligners_count != null || plan.lower_aligners_count != null),
  );
}

function appendCurrentStageDetails(
  items: ClientStatusItem[],
  treatmentId: string,
  stage: Treatment['stages'][number],
  kind: StageKind,
) {
  const action = stageAction(treatmentId, stage.id, kind);

  if (kind === 'preparing') {
    const requiredProcedures = (stage.procedures ?? []).filter((procedure) => procedure.is_required);
    if (requiredProcedures.length === 0) {
      items.push(item('required-procedures', 'attention', 'requiredProceduresMissing', { action }));
      return;
    }
    for (const procedure of requiredProcedures) {
      const values = { title: procedure.title };
      if (procedure.status === 'verified') {
        items.push(item(`procedure-${procedure.id}`, 'complete', 'requiredProcedureReady', { values }));
      } else if (procedure.status === 'completed') {
        items.push(item(`procedure-${procedure.id}`, 'attention', 'requiredProcedureNeedsVerification', { values, action }));
      } else {
        items.push(item(`procedure-${procedure.id}`, 'attention', 'requiredProcedurePending', { values, action }));
      }
    }
    return;
  }

  if (kind === 'modeling') {
    const plans = stage.plan_options ?? [];
    items.push(
      plans.length > 0
        ? item('modeling-plan', 'complete', 'modelingPlanReady')
        : item('modeling-plan', 'attention', 'modelingPlanMissing', { action }),
    );
    items.push(
      hasUsablePublishedPlan(stage)
        ? item('published-plan', 'complete', 'publishedPlanReady')
        : item('published-plan', 'attention', 'publishedPlanMissing', { action }),
    );
    items.push(
      stage.plan_approved_at
        ? item('plan-approval', 'complete', 'planApprovedByPatient')
        : item('plan-approval', 'attention', 'planPendingPatientApproval'),
    );
    return;
  }

  if (kind === 'payment') {
    items.push(item('payment-stage', 'attention', 'paymentStageCurrent', { action }));
    return;
  }

  if (kind === 'manufacturing_delivery') {
    const delivery = stage.delivery ?? null;
    if (!delivery) {
      items.push(item('delivery', 'attention', 'deliveryMissing', { action }));
      return;
    }
    if (delivery.delivery_status === 'cancelled') {
      items.push(item('delivery', 'attention', 'deliveryCancelled', { action }));
      return;
    }
    items.push(item('delivery', 'complete', 'deliveryReady'));
    if (delivery.delivery_status === 'shipped') {
      items.push(item('delivery-status', 'complete', 'deliveryShipped'));
    } else if (delivery.delivery_status === 'delivered') {
      items.push(item('delivery-status', 'complete', 'deliveryDelivered'));
    } else if (delivery.delivery_status === 'pending' || delivery.delivery_status === 'preparing_shipment') {
      items.push(
        delivery.tracking_number?.trim()
          ? item('delivery-tracking', 'complete', 'trackingReady')
          : item('delivery-tracking', 'attention', 'trackingMissing', { action }),
      );
    }
    return;
  }

  const expectedUpper = Math.max(0, stage.upper_aligners_count ?? 0);
  const expectedLower = Math.max(0, stage.lower_aligners_count ?? 0);
  const alignerSets = stage.aligner_sets ?? [];
  const actualUpper = alignerSets.filter((set) => set.jaw_type === 'upper').length;
  const actualLower = alignerSets.filter((set) => set.jaw_type === 'lower').length;
  const resolvedExpectedUpper = stage.upper_aligners_count == null ? actualUpper : expectedUpper;
  const resolvedExpectedLower = stage.lower_aligners_count == null ? actualLower : expectedLower;
  const requiredSetsReady =
    alignerSets.length > 0
    && (resolvedExpectedUpper === 0 || actualUpper >= resolvedExpectedUpper)
    && (resolvedExpectedLower === 0 || actualLower >= resolvedExpectedLower);
  const setValues = {
    actual: actualUpper + actualLower,
    expected: resolvedExpectedUpper + resolvedExpectedLower,
  };
  items.push(
    requiredSetsReady
      ? item('aligner-sets', 'complete', 'alignerSetsReady', { values: setValues })
      : item('aligner-sets', 'attention', 'alignerSetsMissing', { values: setValues, action }),
  );
  items.push(
    stage.wearing_started_at
      ? item('wearing-start', 'complete', 'wearingStarted')
      : item('wearing-start', 'attention', 'wearingNotStarted', { action }),
  );
}

/**
 * Builds advisory client readiness from data already returned by patient and
 * treatment queries. It never authorizes or blocks any workflow action.
 */
export function buildClientStatus(
  patient: ClientStatusPatient,
  treatments: readonly Treatment[],
): readonly ClientStatusItem[] {
  const items: ClientStatusItem[] = [
    patient.tooth_spacing_map_url?.trim()
      ? item('spacing-map', 'complete', 'spacingMapReady')
      : item('spacing-map', 'attention', 'spacingMapMissing', {
          action: { section: 'tooth-spacing', targetId: 'tooth-spacing-map-upload' },
        }),
  ];

  const activeTreatment = treatments.find((treatment) => treatment.status === 'active');
  if (!activeTreatment) {
    const draftTreatment = treatments.find((treatment) => treatment.status === 'draft');
    items.push(item('active-treatment', 'attention', 'activeTreatmentMissing', {
      action: draftTreatment
        ? {
            section: 'treatment-data',
            targetId: `treatment-${draftTreatment.id}-status-action`,
            treatmentId: draftTreatment.id,
          }
        : {
            section: 'treatments',
            targetId: 'create-treatment-status',
            openCreateTreatment: true,
          },
    }));
    return items;
  }

  items.push(item('active-treatment', 'complete', 'activeTreatmentReady'));
  items.push(
    hasDiagnosis(activeTreatment)
      ? item('diagnosis', 'complete', 'diagnosisReady')
      : item('diagnosis', 'attention', 'diagnosisMissing', {
          action: {
            section: 'treatment-data',
            targetId: `treatment-${activeTreatment.id}-diagnosis-action`,
            treatmentId: activeTreatment.id,
          },
        }),
  );

  const activeCycle = activeTreatment.stages.find((stage) => stage.status === 'in_progress');
  if (!activeCycle) {
    const cycleToActivate = activeTreatment.stages.find((stage) => stage.status === 'draft');
    items.push(item('active-cycle', 'attention', 'activeCycleMissing', {
      action: cycleToActivate
        ? {
            section: 'treatments',
            treatmentId: activeTreatment.id,
            stageId: cycleToActivate.id,
            targetId: `stage-tab-${cycleToActivate.id}`,
          }
        : {
            section: 'treatments',
            treatmentId: activeTreatment.id,
            targetId: `treatment-${activeTreatment.id}-add-stage`,
          },
    }));
    return items;
  }

  items.push(item('active-cycle', 'complete', 'activeCycleReady', {
    values: { number: activeCycle.cycle_number },
  }));

  const orderedSteps = [...(activeCycle.steps ?? [])].sort((left, right) => left.sort_order - right.sort_order);
  for (const step of orderedSteps) {
    const values = { stage: STAGE_LABEL_KEYS[step.stage_kind] };
    if (step.status === 'completed') {
      items.push(item(`stage-${step.stage_kind}`, 'complete', 'completedStage', { values }));
    }
  }

  const currentStep = orderedSteps.find(
    (step) => step.stage_kind === activeCycle.current_stage_kind
      && (step.status === 'in_progress' || step.status === 'blocked'),
  ) ?? orderedSteps.find((step) => step.status === 'in_progress' || step.status === 'blocked');
  const currentKind = activeCycle.current_stage_kind ?? currentStep?.stage_kind ?? null;
  if (!currentKind) return items;

  const currentAction = stageAction(activeTreatment.id, activeCycle.id, currentKind);
  items.push(item(
    `current-stage-${currentKind}`,
    'attention',
    currentStep?.status === 'blocked' ? 'blockedStage' : 'currentStage',
    { values: { stage: STAGE_LABEL_KEYS[currentKind] }, action: currentAction },
  ));
  appendCurrentStageDetails(items, activeTreatment.id, activeCycle, currentKind);

  return items;
}
